import { cloneElement, type JSX, type VNode, type TargetedPointerEvent, type TargetedFocusEvent, type TargetedMouseEvent, type TargetedKeyboardEvent } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks';

/** Adds a hover/focus hint without inserting a wrapper into the button's layout. */
export function Tooltip({ text, children }: { text: string; children: VNode<JSX.IntrinsicElements['button']> }) {
  const id = useId();
  const tip = useRef<HTMLDivElement>(null);
  const opening = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closing = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pointerFocus = useRef(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const clearTimers = () => { clearTimeout(opening.current); clearTimeout(closing.current); };
  const hide = () => { clearTimers(); setAnchor(null); setPosition(null); };
  const show = (button: HTMLButtonElement, delay: number) => {
    clearTimers();
    if (delay) opening.current = setTimeout(() => { if (button.isConnected) setAnchor(button); }, delay);
    else setAnchor(button);
  };
  const leave = () => { clearTimers(); closing.current = setTimeout(hide, 120); };

  useLayoutEffect(() => {
    if (!anchor || !tip.current) return;
    const button = anchor.getBoundingClientRect();
    const box = tip.current.getBoundingClientRect();
    const left = Math.max(8, Math.min(button.left + (button.width - box.width) / 2, window.innerWidth - box.width - 8));
    const top = button.top >= box.height + 16 ? button.top - box.height - 8 : button.bottom + 8;
    setPosition({ left, top: Math.max(8, Math.min(top, window.innerHeight - box.height - 8)) });
  }, [anchor, text]);
  useEffect(() => () => clearTimers(), []);
  useEffect(() => {
    if (!anchor) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') hide(); };
    document.addEventListener('keydown', escape);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    window.addEventListener('blur', hide);
    return () => {
      document.removeEventListener('keydown', escape); window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide); window.removeEventListener('blur', hide);
    };
  }, [anchor]);

  const props = children.props;
  return <>
    {cloneElement(children, {
      // Avoid showing a second, delayed browser tooltip over our own hint.
      title: undefined,
      'aria-describedby': [props['aria-describedby'], anchor ? id : null].filter(Boolean).join(' ') || undefined,
      onPointerEnter: (event: TargetedPointerEvent<HTMLButtonElement>) => {
        if (event.pointerType !== 'touch') show(event.currentTarget, 300);
        if (typeof props.onPointerEnter === 'function') props.onPointerEnter(event);
      },
      onPointerLeave: (event: TargetedPointerEvent<HTMLButtonElement>) => { pointerFocus.current = false; leave(); if (typeof props.onPointerLeave === 'function') props.onPointerLeave(event); },
      onFocus: (event: TargetedFocusEvent<HTMLButtonElement>) => {
        if (!pointerFocus.current) show(event.currentTarget, 0);
        pointerFocus.current = false;
        if (typeof props.onFocus === 'function') props.onFocus(event);
      },
      onBlur: (event: TargetedFocusEvent<HTMLButtonElement>) => { pointerFocus.current = false; hide(); if (typeof props.onBlur === 'function') props.onBlur(event); },
      onPointerDown: (event: TargetedPointerEvent<HTMLButtonElement>) => { pointerFocus.current = true; hide(); if (typeof props.onPointerDown === 'function') props.onPointerDown(event); },
      onClick: (event: TargetedMouseEvent<HTMLButtonElement>) => { hide(); if (typeof props.onClick === 'function') props.onClick(event); },
      onKeyDown: (event: TargetedKeyboardEvent<HTMLButtonElement>) => { hide(); if (typeof props.onKeyDown === 'function') props.onKeyDown(event); },
    })}
    {anchor && createPortal(<div class="app-tooltip" id={id} role="tooltip" ref={tip}
      style={position ? position : { left: 0, top: 0, visibility: 'hidden' }} onPointerEnter={clearTimers} onPointerLeave={leave}>{text}</div>, anchor.closest('dialog') ?? document.body)}
  </>;
}
